
import React from 'react';
import { Camera, Shield, Globe, Zap, TrendingUp, Award } from 'lucide-react';

interface ReasonCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
}

const ReasonCard = ({ icon: Icon, title, description }: ReasonCardProps) => {
  return (
    <div 
      className="bg-black/30 backdrop-blur-lg border border-purple-900/20 rounded-xl p-6 hover:bg-black/40 hover:border-purple-900/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-purple-900/10"
      data-aos="fade-up" 
      data-aos-delay="100"
    >
      <div className="w-12 h-12 rounded-full bg-purple-900/30 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-purple-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  );
};

const ReasonSection = () => {
  const reasons = [
    {
      icon: Camera,
      title: "KI anstatt Kamera",
      description: "Kein Schauspieler, kein Studio. Future Level produziert KI-Videos schnell, skalierbar und vollautomatisch."
    },
    {
      icon: Shield,
      title: "Maximale Sicherheit & DSGVO",
      description: "Zertifizierte Datensicherheit und vollständige DSGVO-Konformität für Ihre Videoproduktion."
    },
    {
      icon: Globe,
      title: "Full-Service auf 4 Sprachen",
      description: "Deutsch, Englisch, Französisch, Italienisch – wir liefern Inhalte, die überall verstanden werden."
    },
    {
      icon: Zap,
      title: "Agile Videoproduktion",
      description: "Effiziente und flexible Produktionsprozesse für schnelle Markteinführung Ihrer Videoinhalte."
    },
    {
      icon: TrendingUp,
      title: "Performance-orientiert",
      description: "Unsere Videos sind gemacht für Klicks, Conversions und Kundenbindung."
    },
    {
      icon: Award,
      title: "Innovative KI-Technologie",
      description: "Modernste KI-Algorithmen für hochwertige und kosteneffiziente Videoproduktion."
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-[#111111] to-[#0D0D0D] relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('/images/grid.svg')] bg-center opacity-5"></div>
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-16" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold font-future text-white mb-6">
            6 Gründe für Future Level
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-900 to-blue-900 mx-auto"></div>
        </div>
        
        {/* Grid for desktop */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {reasons.map((reason, index) => (
            <ReasonCard 
              key={index} 
              icon={reason.icon} 
              title={reason.title} 
              description={reason.description} 
            />
          ))}
        </div>
        
        {/* Accordion for mobile */}
        <div className="md:hidden space-y-4 max-w-md mx-auto">
          {reasons.map((reason, index) => (
            <details key={index} className="bg-black/30 backdrop-blur-lg border border-purple-900/20 rounded-xl p-4 group">
              <summary className="flex items-center justify-between cursor-pointer list-none">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-purple-900/30 flex items-center justify-center">
                    <reason.icon className="w-4 h-4 text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white">{reason.title}</h3>
                </div>
                <div className="w-5 h-5 border border-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-purple-500 transform group-open:rotate-45 transition-transform">+</span>
                </div>
              </summary>
              <p className="text-gray-400 mt-3 pl-11">{reason.description}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReasonSection;
